/**
 * The one table every Supplier Performance drill-down renders through.
 *
 * Sized to its content (`w-max`) so the modal body — not this element — is
 * the horizontal scroll container; that keeps the always-visible scrollbar on
 * the modal's bottom edge rather than below a table taller than the viewport.
 */
import { AnyRow, DrilldownColumn, DrilldownFormatters, displayValue } from './columns';

interface DrilldownTableProps {
  rows: AnyRow[];
  columns: DrilldownColumn<AnyRow>[];
  formatters: DrilldownFormatters;
}

function alignClass(align?: 'left' | 'right' | 'center'): string {
  if (align === 'right') return 'text-right';
  if (align === 'center') return 'text-center';
  return 'text-left';
}

function rowKey(row: AnyRow, index: number): string | number {
  const id = row['id'];
  return typeof id === 'number' || typeof id === 'string' ? id : index;
}

export function DrilldownTable({ rows, columns, formatters }: DrilldownTableProps) {
  return (
    <table className="w-max min-w-full text-sm">
      <thead className="sticky top-0 z-10">
        <tr className="border-b bg-muted">
          {columns.map((column) => (
            <th
              key={column.key}
              className={`p-2 font-semibold whitespace-nowrap ${alignClass(column.align)} ${column.title ? 'cursor-help' : ''}`}
              title={column.title}
            >
              {column.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, index) => (
          <tr key={rowKey(row, index)} className="border-b hover:bg-muted/30">
            {columns.map((column) => {
              const text = displayValue(column, row, formatters);
              const clamp = column.truncate ? 'max-w-[220px] truncate' : 'whitespace-nowrap';
              return (
                <td
                  key={column.key}
                  className={`p-2 ${alignClass(column.align)} ${clamp} ${column.className ?? ''}`}
                  title={column.truncate && text !== '-' ? text : undefined}
                >
                  {column.render ? column.render(row) : text}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
